// Country model for the audio-course builder (Michael 2026-10-26/27).
//
// A course = (source language, target language, COUNTRY, subdomain). The country dropdown is
// what this file feeds. The country does NOT change the target voice except for pluricentric
// languages that have a distinct national voice (Spanish: es-ES vs es-US). It drives:
//   1. JURISDICTION — the local laws / health system / government bodies referenced in content.
//   2. The ENGLISH coach accent for the English-speaking diaspora options (US→en-US, GB→en-GB, AU→en-AU).
//   3. targetLocale ONLY where the language is pluricentric and we actually have the voice.
//
// Rule: each target language's dropdown = its native country/countries (limited to voices we have)
// + the universal English-speaking diaspora {US, GB, AU}. De-duped by code (US is both a Spanish
// native market AND a diaspora market → listed once, as native).

export type Country = {
  code: string;        // ISO-ish selector code (ES, US, GB, AU, SA, …)
  label: string;       // dropdown label
  coachLocale: string; // English coach voice locale for this country
  targetLocale?: string; // override target voice locale (pluricentric only)
  native?: boolean;    // a home market of the language (vs English-speaking diaspora)
};

// Universal English-speaking diaspora — the English coach accent differs; the target voice is
// the language's standard (set per language below, or the default voice).
const DIASPORA: Country[] = [
  { code: 'US', label: 'United States', coachLocale: 'en-US' },
  { code: 'GB', label: 'United Kingdom', coachLocale: 'en-GB' },
  { code: 'AU', label: 'Australia', coachLocale: 'en-AU' },
];

// Native markets per TARGET-language FAMILY key (not locale). Only countries we have voices /
// confident jurisdiction for. `targetLocale` set where pluricentric.
const NATIVE: Record<string, Country[]> = {
  // Spanish — the one language with a distinct US national voice.
  SPA: [
    { code: 'ES', label: 'Spain', coachLocale: 'en-GB', targetLocale: 'es-ES', native: true },
    { code: 'US', label: 'United States', coachLocale: 'en-US', targetLocale: 'es-US', native: true },
  ],
  FRA: [{ code: 'FR', label: 'France', coachLocale: 'en-GB', targetLocale: 'fr-FR', native: true }],
  ITA: [{ code: 'IT', label: 'Italy', coachLocale: 'en-GB', targetLocale: 'it-IT', native: true }],
  ELL: [{ code: 'GR', label: 'Greece', coachLocale: 'en-GB', targetLocale: 'el-GR', native: true }],
  EUS: [{ code: 'ES', label: 'Spain', coachLocale: 'en-GB', targetLocale: 'eu-ES', native: true }],
  POR: [
    { code: 'PT', label: 'Portugal', coachLocale: 'en-GB', targetLocale: 'pt-PT', native: true },
    { code: 'BR', label: 'Brazil', coachLocale: 'en-US', targetLocale: 'pt-BR', native: true },
  ],
  // Arabic — standard voice for all; countries are jurisdiction guides (Gulf nursing markets +
  // Egypt), confidence-vetted. No per-country voice.
  ARB: [
    { code: 'SA', label: 'Saudi Arabia', coachLocale: 'en-GB', native: true },
    { code: 'AE', label: 'United Arab Emirates', coachLocale: 'en-GB', native: true },
    { code: 'QA', label: 'Qatar', coachLocale: 'en-GB', native: true },
    { code: 'KW', label: 'Kuwait', coachLocale: 'en-GB', native: true },
    { code: 'BH', label: 'Bahrain', coachLocale: 'en-GB', native: true },
    { code: 'EG', label: 'Egypt', coachLocale: 'en-GB', native: true },
  ],
};

// Map a target-language code (locale or family) to its NATIVE family key above.
export function familyOf(target: string): string {
  const t = (target || '').toUpperCase();
  if (t.startsWith('SPA') || t.startsWith('ES')) return 'SPA';
  if (t.startsWith('FRA') || t.startsWith('FR')) return 'FRA';
  if (t.startsWith('ITA') || t.startsWith('IT')) return 'ITA';
  if (t.startsWith('ELL') || t.startsWith('EL')) return 'ELL';
  if (t.startsWith('EUS') || t.startsWith('EU')) return 'EUS';
  if (t.startsWith('POR') || t.startsWith('PT')) return 'POR';
  if (t.startsWith('AR')) return 'ARB';
  return t;
}

// The country options for a target language: native markets + diaspora (de-duped by code).
export function countriesForTarget(target: string): Country[] {
  const base = NATIVE[familyOf(target)] || [];
  const seen = new Set(base.map((c) => c.code));
  return [...base, ...DIASPORA.filter((d) => !seen.has(d.code))];
}

// Source languages the builder offers (today only English; slot exists for future localizations).
export const SOURCE_LANGUAGES = [{ code: 'EN', label: 'English' }];

// The universal English-speaking diaspora markets, appended to EVERY target language's country
// list (a course can serve its diaspora in the US/UK/Australia). Order fixed.
export const DIASPORA_CODES = ['US', 'GB', 'AU'];

// Human labels for the country codes used across the catalog + diaspora. A country dropdown shows
// the target language's own native country/countries (from the catalog) + the diaspora, de-duped.
export const COUNTRY_LABEL: Record<string, string> = {
  US: 'United States', GB: 'United Kingdom', AU: 'Australia',
  ES: 'Spain', MX: 'Mexico', FR: 'France', IT: 'Italy', GR: 'Greece', DE: 'Germany',
  PT: 'Portugal', BR: 'Brazil', RU: 'Russia', IR: 'Iran', TR: 'Türkiye', EG: 'Egypt',
  GE: 'Georgia', HU: 'Hungary', IL: 'Israel', ID: 'Indonesia', IE: 'Ireland', JP: 'Japan',
  KZ: 'Kazakhstan', KR: 'South Korea', KG: 'Kyrgyzstan', TW: 'Taiwan', MN: 'Mongolia',
  NP: 'Nepal', TZ: 'Tanzania', TH: 'Thailand', UZ: 'Uzbekistan', VN: 'Vietnam', ZA: 'South Africa',
  BG: 'Bulgaria', RO: 'Romania',
};
